import classNames from 'classnames'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import Cross from '../assets/icons/cross.svg'
import { Transition } from './Transition'
import { VideoComponent } from './VideoComponent'

export interface VideoModalProps {
  open: boolean
  onClose: () => void
  videoUrl: string
}

export const VideoModal = ({
  onClose: onCloseCallback,
  open,
  videoUrl,
}: VideoModalProps) => {
  const prevElement = useRef<HTMLElement>()
  const videoRef = useRef<HTMLDivElement>(null)
  const [renderContent, setRenderContent] = useState(false)

  const onClose = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault()
      prevElement.current?.focus()
      onCloseCallback?.()
    },
    [onCloseCallback]
  )

  useEffect(() => {
    prevElement.current = document?.activeElement as HTMLElement
    if (open) {
      setTimeout(() => videoRef.current?.focus(), 0)
    }
  }, [open])

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keyup', handleKey)

    return () => {
      document.removeEventListener('keyup', handleKey)
    }
  }, [onClose])

  return (
    <Transition
      className={classNames(
        'fixed inset-0 z-navbar-search-bar transition-opacity',
        'bg-neutral-contrast/70',
        'flex items-center justify-center'
      )}
      duration={150}
      enterFrom="opacity-0 pointer-events-none"
      enterTo="opacity-100"
      leaveTo="opacity-0 pointer-events-none"
      onClick={onClose}
      onEnter={() => setRenderContent(true)}
      onLeft={() => setRenderContent(false)}
      show={open}
    >
      {/* Conditionally render content so that the video does not autoplay as soon as the page loads */}
      {renderContent && (
        <div
          ref={videoRef}
          tabIndex={-1}
          className="flex-grow max-w-[35.9375rem] sm:max-w-[65.625rem] mx-15 tb:mx-30 sm:mx-60 outline-none text-right"
        >
          <button
            className={classNames(
              'p-15 -mt-45 sm:mb-5 rounded border border-transparent text-neutral bg-neutral',
              'transition-all bg-opacity-0 hover:bg-opacity-20',
              'focus:ring focus:ring-neutral-medium-light/40 focus:outline-none focus:border-current'
            )}
          >
            <Cross className="w-15 h-15 fill-current" />
          </button>

          <div className="bg-neutral-contrast/70" onClick={e => e.stopPropagation()}>
            <VideoComponent autoPlayWithSound title="" videoUrl={videoUrl} />
          </div>
        </div>
      )}
    </Transition>
  )
}
